import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { AppProvider } from './context/AppContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import HomePage from './components/HomePage';
import AdminDashboard from './components/AdminDashboard';
import TournamentManagement from './components/TournamentManagement';
import PlayerManagement from './components/PlayerManagement';
import ProtectedRoute from './components/ProtectedRoute';
import MsalProviderWrapper from './components/MsalProviderWrapper';
import NotificationSystem from './components/common/NotificationSystem';
import { msalConfig } from './config/msalConfig';

// Create a polished theme inspired by Prudential's sophisticated design
const theme = createTheme({
  palette: {
    primary: {
      main: '#1e3a8a', // Deep professional blue (similar to Prudential)
      light: '#3b82f6',
      dark: '#1e40af',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#dc2626', // Refined red accent
      light: '#ef4444',
      dark: '#b91c1c',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc', // Clean light gray background
      paper: '#ffffff', // Pure white paper
    },
    text: {
      primary: '#1f2937', // Dark gray for primary text
      secondary: '#6b7280', // Medium gray for secondary text
    },
    grey: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    success: {
      main: '#059669',
      light: '#10b981',
      dark: '#047857',
    },
    warning: {
      main: '#d97706',
      light: '#f59e0b',
      dark: '#b45309',
    },
    error: {
      main: '#dc2626',
      light: '#ef4444',
      dark: '#b91c1c',
    },
    info: {
      main: '#0284c7',
      light: '#0ea5e9',
      dark: '#0369a1',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontFamily: '"Cinzel", serif',
      letterSpacing: '0.5px',
    },
    h2: {
      fontWeight: 600,
      fontFamily: '"Cinzel", serif',
      letterSpacing: '0.3px',
    },
    h3: {
      fontWeight: 600,
      fontFamily: '"Cinzel", serif',
    },
    h4: {
      fontWeight: 500,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          borderRadius: 12,
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: '#ffffff',
          border: '1px solid #e5e7eb',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 24px',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
        contained: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '@keyframes pulse': {
            '0%': {
              opacity: '0.4',
              transform: 'scale(1)',
            },
            '50%': {
              opacity: '0.8',
              transform: 'scale(1.1)',
            },
            '100%': {
              opacity: '0.4',
              transform: 'scale(1)',
            },
          },
          '@keyframes rotate': {
            '0%': {
              transform: 'translate(-50%, -50%) rotate(0deg)',
            },
            '100%': {
              transform: 'translate(-50%, -50%) rotate(360deg)',
            },
          },
        },
      },
    },
  },
});

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

function App() {
  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <MsalProviderWrapper>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppProvider>
              <Router>
                <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
                              <Routes>
                                <Route path="/" element={<HomePage />} />
                                <Route
                                  path="/admin"
                                  element={
                                    <ProtectedRoute>
                                      <AdminDashboard />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/tournaments"
                                  element={
                                    <ProtectedRoute>
                                      <TournamentManagement />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/players"
                                  element={
                                    <ProtectedRoute>
                                      <PlayerManagement />
                                    </ProtectedRoute>
                                  }
                                />
                              </Routes>
                  <NotificationSystem />
                </Box>
              </Router>
            </AppProvider>
          </ThemeProvider>
        </MsalProviderWrapper>
      </MsalProvider>
    </ErrorBoundary>
  );
}

export default App;
